import React, { useEffect, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { Alert, Box, Button, CircularProgress, Grid, Typography } from '@mui/material';
import { PassengerSlot } from '../../types';
import { getBirthdateAgeMismatchHint } from '../../helpers/passengerSlotDisplay';
import { useSelector } from 'react-redux';
import { RootState } from 'store';
import {
	IPassengerForm,
	PassengerDocumentEnum,
	passengerDocumentType,
	typeNationalites,
} from 'features/salesOrder/types';
import { updatePax, getPassengerId } from 'features/salesOrder/services';
import { nanoidGenerator } from 'features/common/helpers';
import { ControlledTextField } from 'features/common/components/ControlledTextField';
import { ControlledDatePicker } from 'features/common/components/ControlledDatePicker';
import { ControlledSelect } from 'features/common/components/ControlledSelect';
import {
	buildPassengerPayloadForOrder,
	createEmptyPassengerForm,
	mapAutocompleteToPassengerForm,
} from '../../helpers/passengerPayload';
import { extractErrorMessage } from 'features/quotation/helpers';

const nationalityOptions = typeNationalites.map((item) => ({
	id: item.flagsCode,
	name: item.name,
}));

const documentTypeLabel = (type: PassengerDocumentEnum): string =>
	passengerDocumentType.find((item) => item.id === type)?.name ?? type;

interface Props {
	saleOrderId: string;
	slot: PassengerSlot;
	documentType: PassengerDocumentEnum;
	documentNumber: string;
	isMainPassenger: boolean;
	existingPassengerId?: string;
	onSaved: (payload: { keyId: string; name: string; lastName: string; passengerId?: string }) => void;
	onCancel: () => void;
}

export const PassengerCreateForm = ({
	saleOrderId,
	slot,
	documentType,
	documentNumber,
	isMainPassenger,
	existingPassengerId,
	onSaved,
	onCancel,
}: Props): JSX.Element => {
	const { auth } = useSelector((state: RootState) => state);
	const marketCountry = auth.country === 'TN' ? 'PY' : auth.country;
	const [ready, setReady] = useState(!existingPassengerId);
	const [saving, setSaving] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const defaultFormValues = useMemo(
		() =>
			createEmptyPassengerForm({
				keyId: nanoidGenerator(),
				documentCountryCode: marketCountry,
				documentType,
				documentNumber,
				isMainPassenger,
			}),
		[documentNumber, documentType, isMainPassenger, marketCountry],
	);

	const methods = useForm<IPassengerForm>({
		mode: 'onChange',
		defaultValues: defaultFormValues,
	});

	const { reset, handleSubmit, watch } = methods;
	const birthdateValue = watch('birthdate');
	const birthdateHint = getBirthdateAgeMismatchHint(birthdateValue, slot);

	useEffect(() => {
		if (!existingPassengerId) {
			reset(defaultFormValues);
			setReady(true);
			return;
		}

		let active = true;

		const loadExisting = async () => {
			setReady(false);
			try {
				const { data } = await getPassengerId(existingPassengerId);
				if (!active) return;
				reset(mapAutocompleteToPassengerForm(data, data.keyId, isMainPassenger, marketCountry));
				setError(null);
			} catch (e) {
				if (active) {
					setError(extractErrorMessage(e, 'Error al cargar datos del pasajero'));
				}
			} finally {
				if (active) {
					setReady(true);
				}
			}
		};

		void loadExisting();

		return () => {
			active = false;
		};
	}, [defaultFormValues, existingPassengerId, isMainPassenger, marketCountry, reset]);

	const onSubmit = async (form: IPassengerForm) => {
		setSaving(true);
		setError(null);
		try {
			const payload = buildPassengerPayloadForOrder({
				form,
				countryCode: auth.country,
				isMainPassenger,
			});

			await updatePax({
				saleOrderId,
				paxId: form.keyId,
				payload,
			});

			onSaved({
				keyId: form.keyId,
				name: payload.name,
				lastName: payload.lastName,
				passengerId: existingPassengerId,
			});
		} catch (e) {
			setError(extractErrorMessage(e, 'Error al guardar el pasajero en la orden'));
		} finally {
			setSaving(false);
		}
	};

	if (!ready) {
		return (
			<Box display="flex" justifyContent="center" py={4}>
				<CircularProgress />
			</Box>
		);
	}

	return (
		<Box>
			{error && (
				<Alert severity="error" sx={{ mb: 2 }}>
					{error}
				</Alert>
			)}

			<FormProvider {...methods}>
				<Box component="form" onSubmit={handleSubmit(onSubmit)}>
					<Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
						Documento
					</Typography>
					<Grid container spacing={2} sx={{ mb: 2 }}>
						<Grid item xs={12} sm={4}>
							<Typography variant="caption" color="text.secondary" display="block" gutterBottom>
								Tipo
							</Typography>
							<Typography variant="body1">{documentTypeLabel(documentType)}</Typography>
						</Grid>
						<Grid item xs={12} sm={4}>
							<Typography variant="caption" color="text.secondary" display="block" gutterBottom>
								Número
							</Typography>
							<Typography variant="body1">{documentNumber}</Typography>
						</Grid>
						<Grid item xs={12} sm={4}>
							<Typography variant="body2" gutterBottom>
								Nacionalidad del documento *
							</Typography>
							<ControlledSelect
								name="documents.0.country"
								required
								rules={{ required: 'La nacionalidad es requerida' }}
								options={nationalityOptions}
							/>
						</Grid>
					</Grid>

					<Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
						Datos personales
					</Typography>
					<Grid container spacing={2}>
						<Grid item xs={12} sm={6}>
							<Typography variant="body2" gutterBottom>
								Nombre *
							</Typography>
							<ControlledTextField name="name" rules={{ required: 'El nombre es requerido' }} />
						</Grid>
						<Grid item xs={12} sm={6}>
							<Typography variant="body2" gutterBottom>
								Apellido *
							</Typography>
							<ControlledTextField name="lastName" rules={{ required: 'El apellido es requerido' }} />
						</Grid>
						<Grid item xs={12} sm={6}>
							<Typography variant="body2" gutterBottom>
								Fecha de nacimiento *
							</Typography>
							<ControlledDatePicker
								name="birthdate"
								datePurpose="birthdate"
								rules={{ required: 'La fecha de nacimiento es requerida' }}
							/>
							{birthdateHint && (
								<Typography variant="caption" color="warning.main" display="block" sx={{ mt: 0.5 }}>
									{birthdateHint}
								</Typography>
							)}
						</Grid>
						<Grid item xs={12} sm={6}>
							<Typography variant="body2" gutterBottom>
								Vencimiento del documento *
							</Typography>
							<ControlledDatePicker
								name="documents.0.expirationDate"
								datePurpose="documentExpiration"
								rules={{ required: 'La fecha de vencimiento es requerida' }}
							/>
						</Grid>
						<Grid item xs={12} sm={6}>
							<Typography variant="body2" gutterBottom>
								Teléfono
							</Typography>
							<ControlledTextField name="phone" rules={{ required: false }} />
						</Grid>
						<Grid item xs={12} sm={6}>
							<Typography variant="body2" gutterBottom>
								Email
							</Typography>
							<ControlledTextField name="email" rules={{ required: false }} />
						</Grid>
					</Grid>

					<Box display="flex" gap={2} mt={3} justifyContent="flex-end">
						<Button variant="outlined" type="button" onClick={onCancel} disabled={saving}>
							Volver a buscar
						</Button>
						<Button variant="contained" color="success" type="submit" disabled={saving}>
							{saving ? <CircularProgress size={22} color="inherit" /> : 'Guardar y continuar'}
						</Button>
					</Box>
				</Box>
			</FormProvider>
		</Box>
	);
};
